import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, Loader2, CreditCard, BarChart3, Shield,
  Globe, Mail, Eye, EyeOff, Send, CheckCircle2,
  AlertCircle, XCircle, X, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingSection = ({ icon: Icon, title, description, color, children }) => (
  <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
    <div className={`px-6 py-4 border-b border-[#E8EAF0] flex items-center gap-3 bg-gradient-to-r ${color}`}>
      <Icon className="w-5 h-5" />
      <div>
        <h3 className="font-semibold text-[#1A2E6E]">{title}</h3>
        <p className="text-sm text-[#1A2E6E]/60">{description}</p>
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

export const AdminSettings = () => {
  const { refreshSettings } = usePublicSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '',
    site_tagline: '',
    contact_email: '',
    default_currency: 'EUR',
    trial_booking_url: '',
    whatsapp_url: '',
    stripe_public_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    stripe_enabled: false,
    google_analytics_enabled: false,
    google_analytics_id: '',
    gtm_enabled: false,
    gtm_id: '',
    gtm_auth: '',
    gtm_preview: '',
    recaptcha_site_key: '',
    recaptcha_secret_key: '',
    recaptcha_enabled: false,
    recaptcha_score_threshold: 0.5,
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    cancellation_window_hours: 24,
    no_show_deducts_credit: true,
    teacher_noshow_refunds_credit: true,
    reminder_hours_before: 24
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // SMTP state
  const [smtpConfig, setSmtpConfig] = useState({
    enabled: false,
    sender_name: '',
    sender_email: '',
    reply_to: '',
    host: '',
    port: 587,
    encryption: 'tls',
    username: '',
    password: '',
    password_set: false,
  });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpShowPassword, setSmtpShowPassword] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSmtpConfig();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`);
      // Secret fields (stripe_secret_key, stripe_webhook_secret, recaptcha_secret_key) are never
      // returned by the backend — only `*_configured` booleans. Keep the inputs blank; the user
      // must re-enter a value to change a secret (same pattern as the SMTP password field).
      setSettings(prev => ({
        ...prev,
        ...response.data,
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        recaptcha_secret_key: '',
      }));
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!settings.site_name.trim()) {
      toast.error('Site name is required');
      return;
    }

    if (!settings.contact_email.trim()) {
      toast.error('Contact email is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...settings };
      // Never send a blank secret — an empty string here means "unchanged", not "clear it".
      if (!payload.stripe_secret_key) delete payload.stripe_secret_key;
      if (!payload.stripe_webhook_secret) delete payload.stripe_webhook_secret;
      if (!payload.recaptcha_secret_key) delete payload.recaptcha_secret_key;
      await axios.put(`${API_URL}/api/admin/settings`, payload);
      await refreshSettings();
      toast.success('Settings saved successfully');
      fetchSettings(); // refresh to get updated *_configured status and clear the secret inputs
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirmation do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      toast.success('Password updated successfully');
    } catch (err) {
      console.error('Failed to update password:', err);
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchSmtpConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/smtp`);
      setSmtpConfig(prev => ({ ...prev, ...res.data, password: '' }));
    } catch (err) {
      console.error('Failed to fetch SMTP config:', err);
    }
  };

  const handleSmtpChange = (field, value) => {
    setSmtpConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSmtpSave = async () => {
    if (smtpConfig.enabled) {
      if (!smtpConfig.host.trim()) { toast.error('SMTP host is required'); return; }
      if (!smtpConfig.sender_email.trim()) { toast.error('Sender email is required'); return; }
    }
    setSmtpSaving(true);
    try {
      const payload = { ...smtpConfig };
      delete payload.password_set;
      if (!payload.password) delete payload.password; // don't send empty password
      await axios.put(`${API_URL}/api/admin/smtp`, payload);
      toast.success('Email settings saved');
      fetchSmtpConfig(); // refresh to get updated password_set status
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testRecipient.trim()) { toast.error('Enter a recipient email'); return; }
    setTestSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/smtp/test`, { to_email: testRecipient });
      toast.success(`Test email sent to ${testRecipient}`);
      setTestEmailOpen(false);
      setTestRecipient('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  const smtpStatus = (() => {
    if (!smtpConfig.host || !smtpConfig.sender_email) {
      return { label: 'Not configured', color: 'text-[#1A2E6E]/40', Icon: XCircle };
    }
    if (!smtpConfig.enabled) {
      return { label: 'Disabled', color: 'text-[#F5A623]', Icon: AlertCircle };
    }
    return { label: 'Enabled', color: 'text-[#4CAF50]', Icon: CheckCircle2 };
  })();
  const stripeWebhookEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/stripe`;
  const analyticsConflict =
    settings.google_analytics_enabled &&
    settings.gtm_enabled &&
    settings.google_analytics_id &&
    settings.gtm_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Settings
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Configure your platform settings
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="flex items-center gap-2 px-6 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Save Changes
        </button>
      </div>

      {/* Settings Sections */}
      <div className="grid gap-6">
        {/* General Settings */}
        <SettingSection 
          icon={Globe} 
          title="General Settings" 
          description="Basic site information"
          color="from-[#1A2E6E]/5 to-transparent"
        >
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Site Name
              </label>
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => handleChange('site_name', e.target.value)}
                data-testid="setting-site-name"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Tagline
              </label>
              <input
                type="text"
                value={settings.site_tagline}
                onChange={(e) => handleChange('site_tagline', e.target.value)}
                data-testid="setting-site-tagline"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={settings.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                data-testid="setting-contact-email"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Currency
              </label>
              <select
                value={settings.default_currency || 'EUR'}
                onChange={(e) => handleChange('default_currency', e.target.value)}
                data-testid="setting-default-currency"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none bg-white"
              >
                <option value="EUR">EUR (€) — Euro</option>
                <option value="BRL">BRL (R$) — Brazilian Real</option>
                <option value="USD">USD ($) — US Dollar</option>
                <option value="GBP">GBP (£) — British Pound</option>
              </select>
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                Default currency for prices, billing, and reports. New plans and manual payments default to this; existing records keep the currency they were created with.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Trial Booking URL
              </label>
              <input
                type="url"
                value={settings.trial_booking_url || ''}
                onChange={(e) => handleChange('trial_booking_url', e.target.value)}
                placeholder="https://calendar.google.com/..."
                data-testid="setting-trial-booking-url"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                This link powers the main "Book a Trial" CTA on the public site.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                WhatsApp URL
              </label>
              <input
                type="url"
                value={settings.whatsapp_url || ''}
                onChange={(e) => handleChange('whatsapp_url', e.target.value)}
                placeholder="https://wa.me/5511999999999"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                Shown on the student pending screen as "Talk to a consultant via WhatsApp".
              </p>
            </div>
          </div>
        </SettingSection>

        {/* Stripe Payment Settings */}
        <SettingSection
          icon={CreditCard}
          title="Stripe — Online Payments"
          description="Card payment integration for student self-checkout"
          color="from-[#4CAF50]/5 to-transparent"
        >
          <div className="grid gap-4">
            {/* Enable toggle */}
            <label className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[#1A2E6E]">Enable Stripe checkout</p>
                <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                  Shows the "Pay online" button to students after their level test
                </p>
              </div>
              <div
                onClick={() => handleChange('stripe_enabled', !settings.stripe_enabled)}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                  settings.stripe_enabled ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'
                }`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  settings.stripe_enabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </div>
            </label>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Publishable Key <span className="font-normal text-[#1A2E6E]/40">(pk_live_… or pk_test_…)</span>
              </label>
              <input
                type="text"
                value={settings.stripe_public_key || ''}
                onChange={(e) => handleChange('stripe_public_key', e.target.value)}
                placeholder="pk_live_..."
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Secret Key <span className="font-normal text-[#1A2E6E]/40">(sk_live_… or sk_test_…)</span>
                {settings.stripe_configured && !settings.stripe_secret_key && (
                  <span className="ml-2 text-[#4CAF50] font-normal text-xs">Key saved</span>
                )}
              </label>
              <input
                type="password"
                value={settings.stripe_secret_key || ''}
                onChange={(e) => handleChange('stripe_secret_key', e.target.value)}
                placeholder={settings.stripe_configured ? '••••••••••••  (leave blank to keep)' : 'sk_live_...'}
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Webhook Secret <span className="font-normal text-[#1A2E6E]/40">(whsec_…)</span>
                {settings.stripe_webhook_configured && !settings.stripe_webhook_secret && (
                  <span className="ml-2 text-[#4CAF50] font-normal text-xs">Key saved</span>
                )}
              </label>
              <input
                type="password"
                value={settings.stripe_webhook_secret || ''}
                onChange={(e) => handleChange('stripe_webhook_secret', e.target.value)}
                placeholder={settings.stripe_webhook_configured ? '••••••••••••  (leave blank to keep)' : 'whsec_...'}
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                Configure your webhook endpoint in Stripe Dashboard → Developers → Webhooks.
                Endpoint URL: <span className="font-mono">{stripeWebhookEndpoint}</span>
              </p>
              <p className="text-xs text-[#1A2E6E]/50 mt-1">
                Recommended events: <span className="font-mono">checkout.session.completed</span>, <span className="font-mono">checkout.session.expired</span>, <span className="font-mono">payment_intent.payment_failed</span>.
              </p>
            </div>

            {settings.stripe_enabled && (!settings.stripe_public_key || !settings.stripe_configured) && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#F5A623]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Stripe is enabled but keys are missing. Students won't be able to pay online until both keys are set.</span>
              </div>
            )}

            {settings.stripe_enabled && settings.stripe_public_key && settings.stripe_configured && !settings.stripe_webhook_configured && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#C77C08]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Stripe checkout can open without a webhook secret, but automatic payment confirmation and enrollment sync depend on the webhook being configured.
                </span>
              </div>
            )}
          </div>
        </SettingSection>

        {/* Analytics Settings */}
        <SettingSection 
          icon={BarChart3} 
          title="Analytics" 
          description="Google Analytics and Tag Manager"
          color="from-[#F5A623]/5 to-transparent"
        >
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[#1A2E6E]">Enable Google Analytics 4</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                    Use your GA4 Measurement ID directly on the site
                  </p>
                </div>
                <div
                  onClick={() => handleChange('google_analytics_enabled', !settings.google_analytics_enabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.google_analytics_enabled ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    settings.google_analytics_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>
              <label className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[#1A2E6E]">Enable Google Tag Manager</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                    Load your GTM container and optional environment tokens
                  </p>
                </div>
                <div
                  onClick={() => handleChange('gtm_enabled', !settings.gtm_enabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.gtm_enabled ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    settings.gtm_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                GA4 Measurement ID
              </label>
              <input
                type="text"
                value={settings.google_analytics_id || ''}
                onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                data-testid="setting-ga-id"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                Paste the GA4 Measurement ID from Google Analytics Data Streams.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                GTM Container ID
              </label>
              <input
                type="text"
                value={settings.gtm_id || ''}
                onChange={(e) => handleChange('gtm_id', e.target.value)}
                placeholder="GTM-XXXXXXX"
                data-testid="setting-gtm-id"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                  GTM Auth Token <span className="font-normal text-[#1A2E6E]/40">(optional)</span>
                </label>
                <input
                  type="text"
                  value={settings.gtm_auth || ''}
                  onChange={(e) => handleChange('gtm_auth', e.target.value)}
                  placeholder="abc123..."
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
                />
                <p className="text-xs text-[#1A2E6E]/50 mt-2">
                  Required only if you're using a non-default GTM Environment.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                  GTM Preview / Environment Token <span className="font-normal text-[#1A2E6E]/40">(optional)</span>
                </label>
                <input
                  type="text"
                  value={settings.gtm_preview || ''}
                  onChange={(e) => handleChange('gtm_preview', e.target.value)}
                  placeholder="env-3"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
                />
              </div>
            </div>

            {analyticsConflict && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#C77C08]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  GA4 and GTM are both enabled. If your GTM container already fires GA4 pageviews/events, disable direct GA4 here to avoid duplicate tracking.
                </span>
              </div>
            )}

            {settings.google_analytics_enabled && !settings.google_analytics_id && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#C77C08]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>GA4 is enabled but the Measurement ID is still empty.</span>
              </div>
            )}

            {settings.gtm_enabled && !settings.gtm_id && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#C77C08]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>GTM is enabled but the Container ID is still empty.</span>
              </div>
            )}
          </div>
        </SettingSection>

        {/* Security Settings */}
        <SettingSection 
          icon={Shield} 
          title="Security" 
          description="reCAPTCHA and spam protection"
          color="from-[#D72B2B]/5 to-transparent"
        >
          <div className="grid gap-4">
            <div className="p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] text-sm text-[#1A2E6E]/70">
              <p className="font-medium text-[#1A2E6E] mb-1">Google reCAPTCHA v3 setup</p>
              <p>Use score-based reCAPTCHA v3 keys from Google Cloud / reCAPTCHA Admin.</p>
              <p className="mt-1">Protected forms today: <span className="font-medium">Contact</span> and <span className="font-medium">Level Test</span>.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="recaptcha_enabled"
                checked={settings.recaptcha_enabled}
                onChange={(e) => handleChange('recaptcha_enabled', e.target.checked)}
                data-testid="setting-recaptcha-enabled"
                className="w-5 h-5 rounded border-[#D1D5DB] text-[#D72B2B] focus:ring-[#D72B2B]"
              />
              <label htmlFor="recaptcha_enabled" className="text-sm font-medium text-[#1A2E6E]">
                Enable reCAPTCHA on forms
              </label>
            </div>
            <p className="text-xs text-[#1A2E6E]/50">
              reCAPTCHA is only exposed on the public site when both the site key and secret key are configured.
            </p>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                reCAPTCHA Site Key
              </label>
              <input
                type="text"
                value={settings.recaptcha_site_key || ''}
                onChange={(e) => handleChange('recaptcha_site_key', e.target.value)}
                placeholder="6Lc..."
                data-testid="setting-recaptcha-site-key"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                reCAPTCHA Secret Key
                {settings.recaptcha_configured && !settings.recaptcha_secret_key && (
                  <span className="ml-2 text-[#4CAF50] font-normal text-xs">Key saved</span>
                )}
              </label>
              <input
                type="password"
                value={settings.recaptcha_secret_key || ''}
                onChange={(e) => handleChange('recaptcha_secret_key', e.target.value)}
                placeholder={settings.recaptcha_configured ? '••••••••••••  (leave blank to keep)' : '6Lc...'}
                data-testid="setting-recaptcha-secret-key"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Minimum Score Threshold <span className="font-normal text-[#1A2E6E]/40">(0.0 to 1.0)</span>
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={settings.recaptcha_score_threshold ?? 0.5}
                onChange={(e) => handleChange('recaptcha_score_threshold', Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))}
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none font-mono text-sm"
              />
              <p className="text-xs text-[#1A2E6E]/50 mt-2">
                Default is 0.5. Increase it for stricter filtering, or lower it if legitimate leads are being blocked.
              </p>
            </div>

            {settings.recaptcha_enabled && (!settings.recaptcha_site_key || !settings.recaptcha_configured) && (
              <div className="flex items-start gap-2 p-3 bg-[#F5A623]/8 border border-[#F5A623]/20 rounded-xl text-sm text-[#C77C08]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>reCAPTCHA is enabled but the Site Key and Secret Key are not both configured yet.</span>
              </div>
            )}
          </div>
        </SettingSection>

        {/* Social Links */}
        <SettingSection 
          icon={Globe} 
          title="Social Media" 
          description="Your social media links"
          color="from-[#1A2E6E]/5 to-transparent"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Facebook URL
              </label>
              <input
                type="url"
                value={settings.facebook_url || ''}
                onChange={(e) => handleChange('facebook_url', e.target.value)}
                placeholder="https://facebook.com/..."
                data-testid="setting-facebook-url"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Instagram URL
              </label>
              <input
                type="url"
                value={settings.instagram_url || ''}
                onChange={(e) => handleChange('instagram_url', e.target.value)}
                placeholder="https://instagram.com/..."
                data-testid="setting-instagram-url"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                LinkedIn URL
              </label>
              <input
                type="url"
                value={settings.linkedin_url || ''}
                onChange={(e) => handleChange('linkedin_url', e.target.value)}
                placeholder="https://linkedin.com/..."
                data-testid="setting-linkedin-url"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                YouTube URL
              </label>
              <input
                type="url"
                value={settings.youtube_url || ''}
                onChange={(e) => handleChange('youtube_url', e.target.value)}
                placeholder="https://youtube.com/..."
                data-testid="setting-youtube-url"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
              />
            </div>
          </div>
        </SettingSection>

        {/* Scheduling Policy */}
        <SettingSection
          icon={Clock}
          title="Scheduling Policy"
          description="Cancellation windows, no-show rules, and class reminders"
          color="from-[#F5A623]/5 to-transparent"
        >
          <div className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                  Cancellation Window <span className="font-normal text-[#1A2E6E]/40">(hours before class)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="168"
                  value={settings.cancellation_window_hours}
                  onChange={(e) => handleChange('cancellation_window_hours', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
                />
                <p className="text-xs text-[#1A2E6E]/50 mt-1">
                  Students cannot cancel within this window. Set 0 to allow cancellation at any time.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                  Reminder Email <span className="font-normal text-[#1A2E6E]/40">(hours before class)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="168"
                  value={settings.reminder_hours_before}
                  onChange={(e) => handleChange('reminder_hours_before', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
                />
                <p className="text-xs text-[#1A2E6E]/50 mt-1">
                  Automatic reminder sent to enrolled students before each class. Set 0 to disable.
                </p>
              </div>
            </div>
            <div className="grid gap-3">
              <label className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[#1A2E6E]">Deduct credit on student no-show</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                    When a student misses a class without cancelling, deduct 1 credit
                  </p>
                </div>
                <div
                  onClick={() => handleChange('no_show_deducts_credit', !settings.no_show_deducts_credit)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.no_show_deducts_credit ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    settings.no_show_deducts_credit ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>
              <label className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-[#1A2E6E]">Refund credit on teacher no-show</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                    When a teacher misses a class, return 1 credit to each enrolled student
                  </p>
                </div>
                <div
                  onClick={() => handleChange('teacher_noshow_refunds_credit', !settings.teacher_noshow_refunds_credit)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    settings.teacher_noshow_refunds_credit ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    settings.teacher_noshow_refunds_credit ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </SettingSection>

        {/* Email / SMTP Settings */}
        <SettingSection
          icon={Mail}
          title="Email Settings"
          description="Configure SMTP for transactional emails"
          color="from-[#1A2E6E]/5 to-transparent"
        >
          <div className="grid gap-5">
            {/* Status + toggle row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <smtpStatus.Icon className={`w-4 h-4 ${smtpStatus.color}`} />
                <span className={`text-sm font-medium ${smtpStatus.color}`}>{smtpStatus.label}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm font-medium text-[#1A2E6E]">Enable custom SMTP</span>
                <div
                  onClick={() => handleSmtpChange('enabled', !smtpConfig.enabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${smtpConfig.enabled ? 'bg-[#4CAF50]' : 'bg-[#D1D5DB]'}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${smtpConfig.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
              </label>
            </div>

            <div className="p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0] text-sm text-[#1A2E6E]/70">
              <p className="font-medium text-[#1A2E6E] mb-1">Gmail / Google Workspace quick setup</p>
              <p>Host: <span className="font-mono">smtp.gmail.com</span> · Port: <span className="font-mono">587</span> with TLS or <span className="font-mono">465</span> with SSL.</p>
              <p className="mt-1">Use the full Gmail address as username and a Google App Password as the SMTP password.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Sender Name</label>
                <input type="text" value={smtpConfig.sender_name}
                  onChange={e => handleSmtpChange('sender_name', e.target.value)}
                  placeholder="e.g. Frankly Talking English"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Sender Email</label>
                <input type="email" value={smtpConfig.sender_email}
                  onChange={e => handleSmtpChange('sender_email', e.target.value)}
                  placeholder="noreply@yourdomain.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Reply-To Email <span className="text-[#1A2E6E]/40 font-normal">(optional)</span></label>
                <input type="email" value={smtpConfig.reply_to}
                  onChange={e => handleSmtpChange('reply_to', e.target.value)}
                  placeholder="hello@yourdomain.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">SMTP Host</label>
                <input type="text" value={smtpConfig.host}
                  onChange={e => handleSmtpChange('host', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">SMTP Port</label>
                <input type="number" value={smtpConfig.port}
                  onChange={e => handleSmtpChange('port', parseInt(e.target.value) || 587)}
                  placeholder="587"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Encryption</label>
                <select value={smtpConfig.encryption}
                  onChange={e => handleSmtpChange('encryption', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm bg-white">
                  <option value="tls">TLS (STARTTLS) — port 587</option>
                  <option value="ssl">SSL — port 465</option>
                  <option value="none">None — not recommended</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">SMTP Username</label>
                <input type="text" value={smtpConfig.username}
                  onChange={e => handleSmtpChange('username', e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                  SMTP Password
                  {smtpConfig.password_set && !smtpConfig.password && (
                    <span className="ml-2 text-[#4CAF50] font-normal text-xs">Password saved</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={smtpShowPassword ? 'text' : 'password'}
                    value={smtpConfig.password}
                    onChange={e => handleSmtpChange('password', e.target.value)}
                    placeholder={smtpConfig.password_set ? '••••••••  (leave blank to keep)' : 'Enter SMTP password'}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm" />
                  <button type="button" onClick={() => setSmtpShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                    {smtpShowPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button onClick={handleSmtpSave} disabled={smtpSaving}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] transition-colors text-sm font-medium disabled:opacity-50">
                {smtpSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save Email Settings
              </button>
              <button onClick={() => setTestEmailOpen(true)} disabled={!smtpConfig.enabled || !smtpConfig.host || !smtpConfig.sender_email}
                className="flex items-center gap-2 px-5 py-2.5 border border-[#1A2E6E] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                <Send size={16} />
                Send Test Email
              </button>
            </div>

            {smtpConfig.enabled && (!smtpConfig.host || !smtpConfig.sender_email) && (
              <p className="text-xs text-[#D72B2B] flex items-center gap-1">
                <AlertCircle size={13} /> SMTP is enabled but incomplete — add host and sender email to activate.
              </p>
            )}
          </div>
        </SettingSection>

        <SettingSection
          icon={Shield}
          title="Password"
          description="Protect access to the admin portal"
          color="from-[#4CAF50]/5 to-transparent"
        >
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                data-testid="setting-current-password"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                data-testid="setting-new-password"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                data-testid="setting-confirm-password"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
            <button
              type="button"
              onClick={handlePasswordChange}
              disabled={changingPassword}
              data-testid="change-password-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16306F] transition-colors font-medium disabled:opacity-50"
            >
              {changingPassword ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Shield className="w-5 h-5" />
              )}
              Update Password
            </button>
          </div>
        </SettingSection>
      </div>

      {/* Test Email Modal */}
      {testEmailOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EAF0]">
              <h3 className="text-lg font-semibold text-[#1A2E6E]">Send Test Email</h3>
              <button onClick={() => setTestEmailOpen(false)} className="text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#1A2E6E]/60">
                A test email will be sent using your current SMTP configuration to verify the connection.
              </p>
              <div>
                <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={testRecipient}
                  onChange={e => setTestRecipient(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTestEmail()}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
                  autoFocus
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setTestEmailOpen(false)}
                  className="flex-1 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#F8F9FC] transition-colors text-sm font-medium">
                  Cancel
                </button>
                <button onClick={handleTestEmail} disabled={testSending}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] transition-colors text-sm font-medium disabled:opacity-50">
                  {testSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {testSending ? 'Sending…' : 'Send Test'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
